import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import * as moment from 'moment';
import { makeStyles } from '@mui/styles';
import theme from '../../../core/theme';
import { useForm } from 'react-hook-form';
import {
  Autocomplete,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid2,
  IconButton,
  OutlinedInput,
  Radio,
  RadioGroup,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
} from '@mui/material';
import { ContentResponse, ContentRequestParams } from '../../../data/types/content.interface';
import { CloseIcon, DownIcon, NotInUseIcon, UnusedIcon, UpIcon, UsedIcon } from '../../shared/components/AppIcons';
import { capitalize, isEmpty } from 'lodash';
import ConfirmationDialog from '../../shared/components/ConfirmationDialog';
import ContentsService from '../../content-creation/services/contents.services';
import VideoPlayer from '../../shared/components/VideoPlayer';
import { TagType } from '../../../data/types/tags.interface';

const useStyles = makeStyles({
  paper: {
    minWidth: '909px !important',
    height: 'auto',
    borderRadius: '21px',
    boxShadow: 'rgba(242, 242, 242, 1)',
    '& .MuiInputBase-input, .MuiOutlinedInput-input': {
      font: 'Lato !important',
    },
  },
  header: {
    fontSize: '24px',
    verticalAlign: 'text-top',
  },
  close: {
    fontSize: '50px !important',
    color: '#5F6774 !important',
    '& path': {
      fill: `#2C2C2C !important`,
    },
  },
  progressBar: {
    '& .MuiSvgIcon-root': {
      color: '#4db193 !important',
    },
    '& .MuiStepLabel-label': {
      fontFamily: 'Lato !important',
    },
  },
  smallText: {
    fontSize: '12px',
    color: theme.palette.outlineInactive.main,
  },
  input: {
    backgroundColor: '#fff',
    borderRadius: '5px',
    height: '40px',
    paddingLeft: '14px',
    border: '1px solid #E1E3E8 !important',
    '&:focus-visible': {
      border: '1px solid #E1E3E8 !important',
      outline: 'none',
    },
  },
  outlineInputField: {
    '& .MuiOutlinedInput-root': {
      minHeight: 40,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#E1E3E8 !important',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#E1E3E8 !important',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#E1E3E8 !important',
      borderWidth: '1px !important',
    },
    '& .MuiOutlinedInput-input': {
      outline: 'none !important',
      textOverflow: 'ellipsis',
    },
  },
  cancel: {
    color: '#F67570 !important',
    marginRight: '24px !important',
    fontSize: '16px !important',
    fontFamily: 'Lato !important',
  },
  save: {
    width: '135px',
    height: '38px',
    fontSize: '18px !important',
    backgroundColor: '#F67570 !important',
    color: '#fff !important',
    boxShadow: 'none !important',
    '& g path': {
      fill: '#ffffff !important',
    },
    '& .error': {
      border: '1px solid red',
    },
  },
  list: {
    margin: '11px 0px 11px 34px',
    '& .MuiStack-root': {
      width: '250px',
      border: '1px solid #E1E3E8',
    },
  },
  downIcon: {
    '& path': {
      fill: '#000 !important',
    },
  },
  path: {
    border: '1px solid #DE5852',
    borderRadius: '4px',
    wordBreak: 'break-all',
    width: '100%',
    height: '40px',
    alignItems: 'center',
  },
  wrapText: {
    '& input': {
      wordBreak: 'break-word',
    },
  },
  radioBtn: {
    '& span': {
      '& svg': {
        fill: '#E1E3E8 !important',
      },
    },
    '& .Mui-checked': {
      '& svg': {
        fill: '#30343F !important',
      },
    },
  },
  overflow: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
});

interface Props {
  setOpenRawAssetDetailsModal: Dispatch<SetStateAction<boolean>>;
  id: number | null;
  setRefresh?: Dispatch<SetStateAction<boolean>>;
  setToasterMessage?: Dispatch<SetStateAction<string>>;
  disableEdit?: boolean;
  themeList: TagType[];
}

const steps = [
  'Upload Raw File',
  'Mention Title Name',
  'Mention Date Added and Raw Asset Link',
  'Used/Unused',
  'To Content Creation',
];

const ContentDetails: React.FC<Props> = ({ setOpenRawAssetDetailsModal, id, setRefresh, setToasterMessage, disableEdit, themeList }) => {
  const classes = useStyles();
  const [cont, setCont] = useState<ContentResponse | null>();
  const [errorMessage, setErrorMessage] = useState<string>('');
  const {
    register,
    setValue,
    formState: { errors },
  } = useForm({ mode: 'all' });
  const [title, setTitle] = useState(cont?.title || '');
  const [isDirtyTitle, setIsDirtyTitle] = useState(false);
  const [fileName, setFileName] = useState(cont?.filename || '');
  const [isDirtyFileName, setIsDirtyFileName] = useState(false);
  const [folder, setFolder] = useState<string>('');
  const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);
  const [usedOrUnused, setUsedOrUnused] = useState(cont?.edited);
  const [contentAccessLink, setContentAccessLink] = useState('');
  const [anchorEl, setAnchorEl] = React.useState(false);
  const [selectedTheme, setSelectedTheme] = useState<TagType | null>(null);

  const theme = cont?.theme || cont?.themes?.find((t: TagType) => t.type === 'theme')?.name;

  const defaultThemeProps = {
    options: themeList,
    getOptionLabel: (option: TagType) => option.name,
  };

  const getContentDetails = async (id: number) => {
    const content = await ContentsService.getDetails(id);
    if (content) {
      setCont(content);
      setTitle(content.title);
      setFileName(content.filename);
      setUsedOrUnused(content.edited);
      setFolder(content.folder || '');
      setValue('title', content.title);
      setValue('filename', content.filename);
    }
  };

  const fetchRealContent = async (url: string) => {
    let contentUrl = '';
    if (url) {
      if (url.includes('localhost')) {
        const accessContent = await ContentsService.fetchRealS3Content(url);
        setContentAccessLink(accessContent.url);
      } else {
        contentUrl = await ContentsService.fetchRealDropboxContent(url);
        setContentAccessLink(contentUrl);
      }
    }
  };

  useEffect(() => {
    getContentDetails(Number(id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (cont?.videoUrl && !cont?.videoUrl.includes('HEIC')) {
      fetchRealContent(cont?.videoUrl);
    }
  }, [cont]);

  const handleConfirm = async () => {
    try {
      const params: ContentRequestParams = {};
      params.folder = folder;
      if (isDirtyFileName) {
        params.filename = fileName;
      }
      if (isDirtyTitle) {
        params.title = title;
      }
      params.usedOrUnused = usedOrUnused;
      if (selectedTheme && cont?.themes?.filter((t) => t.type === 'theme').length === 0) {
        params.themes = [selectedTheme];
      }
      const content = await ContentsService.updateContentDetails(Number(id), 'raw', params);
      if (content) {
        setOpenRawAssetDetailsModal(false);
        if (setRefresh) {
          setRefresh(true);
        }
        setIsDirtyFileName(false);
        setIsDirtyTitle(false);
        setShowConfirmationModal(false);
        setCont(content);
        getContentDetails(content.id);
      }
    } catch (error) {
      setErrorMessage(error?.response?.data[0]?.message || 'Please try again');
    }
  };

  const handleSave = () => {
    if (isDirtyFileName) {
      setShowConfirmationModal(true);
    } else handleConfirm();
    if (setToasterMessage) {
      setToasterMessage('Changes Saved Successfully');
    }
  };

  const handleFileName = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsDirtyFileName(true);
    if (e.target.value.endsWith('Raw')) {
      setFileName(e.target.value || '');
      setErrorMessage('');
    } else {
      setErrorMessage('Please enter file name as per the naming convention');
    }
  };

  const handleTitle = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsDirtyTitle(true);
    if (e.target.value.endsWith('Title_Raw')) {
      setTitle(e.target.value);
      setErrorMessage('');
    } else {
      setErrorMessage('Please enter title as per the naming convention');
    }
  };

  const handleUsedOrUnused = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUsedOrUnused(e.target.value);
  };

  const handleThemeChange = (e: React.SyntheticEvent<Element, Event>, values: TagType | null) => {
    setSelectedTheme(values);
  };

  const getAccessLink = (url: string) => {
    const dropboxPath = url.split('/V2 Solutions Team')[1];
    const filename = cont?.filename.split('-')[0];

    return `https://www.dropbox.com/home/V2%20Solutions%20Team%2F${dropboxPath.split(`/${filename}`)[0]
      }?preview=${filename}`;
  };

  return (
    <>
      <Dialog
        open
        aria-modal="true"
        role="dialog"
        PaperProps={{
          onClick: (e: any) => e.stopPropagation(),
          className: classes.paper,
        }}
      >
        <DialogTitle>
          <Grid2 container justifyContent="space-between" alignItems="center">
            <Grid2>
              <span className={classes.header}>Raw Asset Details</span>
            </Grid2>
            <Grid2 container justifyContent="flex-end">
              <IconButton onClick={() => setOpenRawAssetDetailsModal(false)} role="button" sx={{ padding: 0 }}>
                <CloseIcon className={classes.close} />
              </IconButton>
            </Grid2>
          </Grid2>
          <Grid2>
            <Divider
              orientation="horizontal"
              flexItem
              sx={{
                backgroundColor: '#E1E3E8',
                paddingBottom: '1px',
              }}
            />
          </Grid2>
        </DialogTitle>
        <DialogContent sx={{ marginTop: '32px' }}>
          {errorMessage && errorMessage.length > 0 && (
            <Grid2 container justifyContent="center">
              <div className="text-danger mb-1 pb-5 text-center">{errorMessage}</div>
            </Grid2>
          )}
          <Grid2 container direction="column" className={classes.progressBar}>
            <Stepper activeStep={4} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Grid2>

          <Grid2 container mt={4}>
            <Grid2 container width="74%">
              <span className={classes.smallText}>
                <VideoPlayer videoUrl={contentAccessLink || ''} thumbnail={cont?.thumbnail} />
              </span>
            </Grid2>

            <Grid2 style={{ cursor: 'pointer' }} container direction="column" ml={3} width="23%">
              <Grid2>
                <span>Usage</span>
              </Grid2>
              <Grid2 container width="100%" justifyContent="space-between">
                <Grid2 width="80%">
                  {usedOrUnused === 'Unused' ? (
                    <span>
                      <UnusedIcon viewBox="5 6 32 32" sx={{ fontSize: '42px' }} />
                      Not Used
                    </span>
                  ) : usedOrUnused === 'NotInUse' ? (
                    <span>
                      <NotInUseIcon viewBox="5 6 32 32" sx={{ fontSize: '42px' }} />
                      Not In Use
                    </span>
                  ) : (
                    <span>
                      <UsedIcon viewBox="5 6 32 32" style={{ fontSize: '42px' }} />
                      Used
                    </span>
                  )}
                </Grid2>
                <Grid2 width="20%">
                  <Button onClick={() => setAnchorEl(!anchorEl)}>
                    {anchorEl ? (
                      <UpIcon style={{ fontSize: '35px' }} className={classes.downIcon} />
                    ) : (
                      <DownIcon style={{ fontSize: '35px' }} className={classes.downIcon} />
                    )}
                  </Button>
                </Grid2>
              </Grid2>
              {anchorEl && (
                <FormControl>
                  <RadioGroup
                    aria-labelledby="demo-controlled-radio-buttons-group"
                    name="controlled-radio-buttons-group"
                    value={usedOrUnused}
                    onChange={handleUsedOrUnused}
                  >
                    <Grid2 container direction="column">
                      <FormControlLabel
                        sx={{ ml: '4px' }}
                        className={classes.radioBtn}
                        value="Used"
                        control={<Radio />}
                        label="Used"
                      />
                      <FormControlLabel
                        sx={{ ml: '4px' }}
                        className={classes.radioBtn}
                        value="Unused"
                        control={<Radio />}
                        label="Not Used"
                      />
                      <FormControlLabel
                        sx={{ ml: '4px' }}
                        className={classes.radioBtn}
                        value="NotInUse"
                        control={<Radio />}
                        label="Not In Use"
                      />
                    </Grid2>
                  </RadioGroup>
                </FormControl>
              )}
            </Grid2>
          </Grid2>

          <Grid2 container direction="column">
            <span className={classes.smallText} style={{ color: 'black' }}>
              Date Added: {moment(cont?.createdAt).format('YYYY-MM-DD')}
            </span>
            <span className={classes.smallText} style={{ color: 'black' }}>
              Last Updated on Dropbox: {moment(cont?.lastUpdatedAt).format('YYYY-MM-DD')}
            </span>
          </Grid2>

          <Grid2 container direction="column" flexWrap="nowrap" mt={2}>
            <Grid2 container direction="column">
              <label>Raw File*</label>
              <input
                {...register('filename', { required: true })}
                name="filename"
                id="filename"
                className={classes.input}
                onChange={(e) => handleFileName(e)}
              />
            </Grid2>
            <Grid2 container direction="column" mt={2}>
              <label>Title*</label>
              <input
                {...register('title', { required: true })}
                name="title"
                id="title"
                className={classes.input}
                onChange={(e) => handleTitle(e)}
              />
            </Grid2>
          </Grid2>

          <Grid2 container>
            <Grid2 container direction="column" sx={{ width: '50%' }}>
              {cont?.assetContentType && (
                <Grid2 container direction="column" mt={1.5}>
                  <label>Content Type</label>
                  <FormControl>
                    <OutlinedInput
                      placeholder="contentType"
                      className={classes.outlineInputField}
                      value={`${capitalize(cont?.assetContentType.name)}`}
                    />
                  </FormControl>
                </Grid2>
              )}
            </Grid2>
            <Grid2 container direction="column" mt={1.5} ml={2} sx={{ width: '48%' }}>
              <label>Theme</label>
              {theme ? (
                <FormControl>
                  <OutlinedInput
                    className={classes.outlineInputField}
                    placeholder="Theme"
                    value={theme}
                  />
                </FormControl>
              ) : (
                <Stack spacing={1} sx={{ width: 300 }}>
                  <Autocomplete
                    {...defaultThemeProps}
                    id="auto-highlight"
                    autoHighlight
                    value={selectedTheme}
                    onChange={handleThemeChange}
                    className={`form-control ${errors.theme && 'is-invalid'}`}
                    renderInput={(params) => <TextField {...params} label="" variant="standard" />}
                  />
                </Stack>)
              }
            </Grid2>
          </Grid2>

          <Grid2 container>
            <Grid2 container direction="column" mt={1.5} sx={{ width: '49%' }}>
              <label>Category</label>
              <FormControl>
                <OutlinedInput className={classes.outlineInputField} placeholder="category" value={cont?.category} />
              </FormControl>
            </Grid2>
            <Grid2 container direction="column" mt={1.5} ml="7px" sx={{ width: '50%' }}>
              <label>Sub Category</label>
              <FormControl>
                <OutlinedInput
                  className={classes.outlineInputField}
                  placeholder="sub category"
                  value={cont?.subCategory}
                />
              </FormControl>
            </Grid2>
          </Grid2>

          <Grid2 container direction="column" mt={1.5}>
            <label>Main Folder</label>
            <FormControl>
              <OutlinedInput className={classes.outlineInputField} placeholder="main folder" value={cont?.mainFolder} />
            </FormControl>
          </Grid2>

          <Grid2 container direction="column" mt={1.5}>
            <label>Sub Folder</label>
            <FormControl>
              <OutlinedInput className={classes.outlineInputField} placeholder="sub folder" value={cont?.subFolder} />
            </FormControl>
          </Grid2>

          <Grid2 container direction="column" mt={1.5}>
            <label>Breadcrumb</label>
            <FormControl fullWidth sx={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>
              <TextField
                placeholder="breadcrumb"
                value={cont?.pathLink}
                variant="outlined"
                multiline
                fullWidth
                InputProps={{
                  readOnly: true,
                }}
                minRows={2}
                maxRows={4}
                sx={{ height: 'auto', wordBreak: 'break-word' }}
                className={classes.outlineInputField}
              />
            </FormControl>
          </Grid2>

          <Grid2 container direction="column" mt={2}>
            <label>Path</label>
            <Grid2 container mt={1} className={classes.path} p={0.5}>
              {cont && (
                <a
                  href={cont.videoUrl.includes('localhost') ? contentAccessLink : getAccessLink(cont.videoUrl)}
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: '#DE5852', textDecoration: 'none' }}
                  className={classes.overflow}
                >
                  {cont?.videoUrl}
                </a>
              )}
            </Grid2>
          </Grid2>

          <Grid2>
            <Divider
              orientation="horizontal"
              flexItem
              sx={{
                backgroundColor: '#E1E3E8',
                paddingBottom: '1px',
              }}
            />
          </Grid2>

          <Grid2 container mt={4} justifyContent="end">
            <Button
              size="small"
              variant="text"
              type="button"
              className={classes.cancel}
              onClick={() => setOpenRawAssetDetailsModal(false)}
            >
              cancel
            </Button>
            <Button
              size="small"
              className={`${classes.save} ${!isEmpty(errors) ? 'disable' : ''}`}
              variant="contained"
              onClick={handleSave}
              disabled={!isEmpty(errorMessage) || disableEdit}
            >
              <span style={{ fontSize: 'h5', textTransform: 'none' }}>Save Changes</span>
            </Button>
          </Grid2>
        </DialogContent>
      </Dialog>
      {showConfirmationModal && (
        <ConfirmationDialog action="rename" confirm={handleConfirm} setShowAlert={setShowConfirmationModal} />
      )}
    </>
  );
};

export default ContentDetails;
